import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@mozioinc/ui";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowSvg } from "./assets/arrow.svg";
import { ReactComponent as CancelSvg } from "./assets/cancel.svg";
import { Dialog } from "./dialog";
import type { TransNamespace } from "../types";

type CancelationDialogProps = {
  open: boolean;
  amount: string;
  onCancel: (reason: string) => void;
  onClose: () => void;
};

const SOMETHING_ELSE_VALUE = "Something else";

export const CancelationDialog: React.FC<CancelationDialogProps> = ({
  open,
  amount,
  onCancel,
  onClose,
}) => {
  const { t } = useTranslation<TransNamespace>("parking:cancelation-dialog");
  const [inputReason, setInputReason] = useState("");
  const [selectedReason, setSelectedReason] = useState<string>("");

  const handleCancel = () => {
    onCancel(
      selectedReason === SOMETHING_ELSE_VALUE ? inputReason : selectedReason,
    );
  };

  const REASONS_FOR_CANCELING = [
    {
      value: "I changed my travel dates",
      content: t("I changed my travel dates"),
    },
    {
      value: "My flights got canceled",
      content: t("My flights got canceled"),
    },
    {
      value: "I’m no longer driving to the airport",
      content: t("I’m no longer driving to the airport"),
    },
    {
      value: "I found cheaper parking",
      content: t("I found cheaper parking"),
    },
    {
      value: SOMETHING_ELSE_VALUE,
      content: t("Something else"),
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        icon={<CancelSvg className="text-primary text-6xl" />}
      >
        <h2 className="mb-1 mt-7 text-2xl font-bold leading-9">
          {t("Confirm cancelation")}
        </h2>
        <p className="mb-8">
          {t("You will be refunded the entire amount of {{amount}}", {
            amount: `$${amount}`,
          })}
        </p>
        <Select onValueChange={(value) => setSelectedReason(value)}>
          <SelectTrigger className="mb-4">
            <SelectValue placeholder={t("Reason for canceling")} />
            <SelectIcon asChild>
              <ArrowSvg className="text-primary" />
            </SelectIcon>
          </SelectTrigger>
          <SelectContent>
            {REASONS_FOR_CANCELING.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.content}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedReason === SOMETHING_ELSE_VALUE && (
          <Input
            placeholder={t("Other reason")}
            type="text"
            className="mb-4"
            value={inputReason}
            onChange={(event) => setInputReason(event.target.value)}
          />
        )}
        <Button
          className="w-full"
          disabled={
            selectedReason === "" ||
            (selectedReason === SOMETHING_ELSE_VALUE && inputReason === "")
          }
          onClick={handleCancel}
        >
          {t("Cancel reservation")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setSelectedReason("");
            onClose();
          }}
        >
          {t("Go back")}
        </Button>
        <p className="mb-8 text-xs">
          {t(
            "By canceling the reservation you agree with our Terms and Conditions",
          )}
        </p>
      </Dialog>
    </>
  );
};
